import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Container from './Container';

/**
 * MenuHeroSlideshow component for displaying a slideshow of menu category images
 * with styling similar to the menu category hero
 * 
 * @param {Object} props Component props
 * @param {Array} props.categories Array of menu categories with images
 * @returns {JSX.Element} MenuHeroSlideshow component
 */
const MenuHeroSlideshow = ({ categories }) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [slides, setSlides] = useState([]);
  
  // Filter categories with images and shuffle them
  useEffect(() => {
    if (categories && categories.length > 0) {
      // Filter categories with images
      const categoriesWithImages = categories.filter(cat => cat.imageUrl);
      
      // Shuffle the array to randomize the slideshow
      const shuffled = [...categoriesWithImages].sort(() => 0.5 - Math.random());
      
      // Use up to 5 categories for the slideshow
      setSlides(shuffled.slice(0, 5));
    }
  }, [categories]);
  
  // Auto-advance slides every 5 seconds
  useEffect(() => {
    if (slides.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentSlideIndex(prevIndex => (prevIndex + 1) % slides.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [slides.length]);
  
  // If no slides available, return a fallback
  if (slides.length === 0) {
    return (
      <div className="relative h-[400px] bg-brand-navy">
        {/* Gradient Overlay - Darker on mobile for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/60 to-brand-navy/30 lg:via-transparent lg:to-transparent"></div>
        
        <Container className="relative h-full">
          <div className="absolute bottom-8 left-0 right-0 w-full px-6 md:px-8 lg:px-12">
            <div className="flex flex-col md:flex-row md:justify-between md:items-end">
              <div>
                <h1 className="text-5xl md:text-6xl font-heading text-white drop-shadow-lg">Our Menu</h1>
                <p className="text-white text-opacity-90 mt-4 max-w-2xl drop-shadow-md">
                  Our menu is full of hand-crafted breakfast favorites and dishes with a unique twist that you will only find at OPH.
                </p>
              </div>
            </div>
          </div>
        </Container>
      </div>
    );
  }
  
  return (
    <div className="relative h-[400px] overflow-hidden">
      {/* Slideshow - Direct image swap without fade transition */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${slides[currentSlideIndex].imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transition: 'none'
        }}
      />
      
      {/* Gradient Overlay - Darker on mobile for better text readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/60 to-brand-navy/30 lg:via-transparent lg:to-transparent"></div>
      
      {/* Content */}
      <Container className="relative h-full">
        <div className="absolute bottom-8 left-0 right-0 w-full px-6 md:px-8 lg:px-12">
          <div className="flex flex-col md:flex-row md:justify-between md:items-end">
            {/* Left side - Title */}
            <div className="mb-4 md:mb-0">
              <h1 className="text-5xl md:text-6xl font-heading text-white drop-shadow-lg">Our Menu</h1>
            </div>
            
            {/* Right side - Description */}
            <div className="md:max-w-xl">
              <p className="text-white text-opacity-90 drop-shadow-md">
                Our menu is full of hand-crafted breakfast favorites and dishes with a unique twist that you will only find at OPH.
              </p>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

MenuHeroSlideshow.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
      imageUrl: PropTypes.string,
    })
  ).isRequired,
};

export default MenuHeroSlideshow;
